import { CaretRight } from '@phosphor-icons/react';
import classnames from 'classnames';
import type { CSSProperties } from 'react';
import { ParagraphElement, type TParagraphElement } from '../paragraphElement';
import type { AlignType } from '../types';
import { breadcrumbs, breadcrumbsText } from './Breadcrumbs.css';
import type { BreadcrumbsElementProps } from './Breadcrumbs.types';

export function BreadcrumbsElement(props: BreadcrumbsElementProps) {
  const { element, attributes } = props;

  const {
    data,
    color,
    fontFamily,
    textAlign,
    mobileTextAlign,
    fontSize,
    mobileFontSize,
    fontWeight,
    lineHeight,
    mobileLineHeight,
    textTransform,
    letterSpacing,
    padding,
    mobilePadding,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    caretColor,
    caretSize,
  } = element.attrs || {};

  const createBreadcrumbTextElement = (): TParagraphElement => ({
    type: 'paragraph',
    attrs: {
      id: `breadcrumb-${Math.random().toString(36).substr(2, 9)}`,
      textAlign: (textAlign as AlignType) || 'left',
      mobileTextAlign: mobileTextAlign as AlignType,
      padding: padding || '0px 0px 0px 0px',
      mobilePadding: mobilePadding,
      lineHeight: lineHeight || '120%',
      mobileLineHeight: mobileLineHeight,
      backgroundColor: 'transparent',
      borderRadius: '0px',
      borderWidth: '0px',
      borderColor: 'inherit',
      borderStyle: 'none',
      lineClampClass: '',
      margin: '0px 0px 0px 0px',
      textTransform: textTransform || 'none',
      letterSpacing: letterSpacing || '0px',
    },
    content: [],
  });

  const textStyle = {
    color: color || '#111827',
    fontFamily: fontFamily || 'Arial',
    fontWeight: fontWeight || '600',
    textTransform: textTransform || 'none',
    letterSpacing: letterSpacing || '0px',
    ...(mobileFontSize && {
      '@media (max-width: 767px)': {
        fontSize: mobileFontSize,
        lineHeight: mobileLineHeight || lineHeight || '120%',
      },
    }),
  };

  const containerStyle = {
    padding: padding || '0px 0px 0px 0px',
    backgroundColor: backgroundColor || 'transparent',
    borderRadius: borderRadius || '0px',
    borderWidth: borderWidth || '0px',
    borderColor: borderColor || 'inherit',
    borderStyle: borderStyle || 'none',
    ...(mobilePadding && {
      '@media (max-width: 767px)': {
        padding: mobilePadding,
      },
    }),
  };

  return (
    <div className={classnames('relative w-full max-w-full my-4', breadcrumbs)} style={containerStyle} {...attributes}>
      <ul className="flex items-center gap-2 text-xs font-semibold min-w-0">
        {data?.breadcrumbs.map((item, index) => (
          <li
            key={`${item.text}-${index}`}
            className={classnames('flex items-center gap-2', {
              'flex-shrink-0': index !== data.breadcrumbs.length - 1,
              'flex-shrink min-w-0 flex-1': index === data.breadcrumbs.length - 1,
            })}
          >
            {index === data.breadcrumbs.length - 1 ? (
              <ParagraphElement element={createBreadcrumbTextElement()} as="span" className="truncate block">
                <span
                  className={classnames(breadcrumbsText)}
                  style={
                    {
                      ...textStyle,
                      '--font-size': fontSize || '12px',
                      '--line-height': lineHeight || '120%',
                      '--mobile-font-size': mobileFontSize || '12px',
                      '--mobile-line-height': mobileLineHeight || '120%',
                    } as CSSProperties
                  }
                >
                  {item.text}
                </span>
              </ParagraphElement>
            ) : item.href ? (
              <a className="truncate block" href={item.href}>
                <ParagraphElement element={createBreadcrumbTextElement()} as="span" className="truncate block">
                  <span
                    className={classnames(breadcrumbsText)}
                    style={
                      {
                        ...textStyle,
                        '--font-size': fontSize || '12px',
                        '--line-height': lineHeight || '120%',
                        '--mobile-font-size': mobileFontSize || '12px',
                        '--mobile-line-height': mobileLineHeight || '120%',
                      } as CSSProperties
                    }
                  >
                    {item.text}
                  </span>
                </ParagraphElement>
              </a>
            ) : (
              <ParagraphElement element={createBreadcrumbTextElement()} as="span" className="truncate block">
                <span
                  className={classnames(breadcrumbsText)}
                  style={
                    {
                      ...textStyle,
                      '--font-size': fontSize || '12px',
                      '--line-height': lineHeight || '120%',
                      '--mobile-font-size': mobileFontSize || '12px',
                      '--mobile-line-height': mobileLineHeight || '120%',
                    } as CSSProperties
                  }
                >
                  {item?.text || ''}
                </span>
              </ParagraphElement>
            )}
            {index !== data.breadcrumbs.length - 1 && (
              <CaretRight
                size={caretSize || '14px'}
                className="flex-shrink-0"
                style={{ color: caretColor || '#6B7280' }}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
